import { groupBy, partition, differenceBy, sortBy, clamp, isEmpty } from 'lodash-es';
import { DateTime } from 'luxon';
import type { Signal } from '@bayer-int/portletce-aclibv2';
import { BWQuerySystem, isString, last } from '@bayer-int/portletce-shared';

import { isNotNullable } from 'portlet/common/utils';
import type {
    FilterData,
    SettingValueMap,
    FilterIdToSignalMapping,
    FilterType,
} from 'portlet/models';
import { FilterValue } from 'portlet/models/filterValue';
import { QueryChartInteractionDatapointUtil } from 'portlet/services/InteractionToFilterService/QueryChartInteractionDatapoint';

import type {
    BWQuery,
    BWQueryParameters,
    ConditionQueryItems,
    QueryFilterDataItem,
    QuerySettingDataItem,
} from './types';
import { BWQueryC, DrillLogic, SettingType } from './types';
import type { MatchedListenToQueryContext, PortletQueryDefinition } from '../types';

const DEFAULT_DRILL_LOGIC: keyof typeof DrillLogic = 'add';
const DATE_FORMAT = 'yyyy-MM-dd';
const HIERARCHY_VARIANT_SUFFIX = '__VAR';

export const BWSystemDefinition: PortletQueryDefinition<BWQuery, BWQueryParameters> = {
    codec: BWQueryC,

    build: (metadata, settings, filters, signals, listenToSignalMapping) => {
        const queryId = metadata.id;
        const { conditionQueryItem, conditionQueryFilter } = getConditionQueryItems(
            settings,
            metadata,
        );
        const queryFilters = getActiveQueryFilters(
            signals,
            filters,
            metadata,
            listenToSignalMapping,
            conditionQueryFilter,
            formatRangeBW,
            formatMultipleValuesBW,
        );

        const activeQueryFilters = settings.HIGHLIGHT_ON_FILTER_IN_CURRENT_DRILL
            ? omitFiltersInCurrentDrill(queryFilters, settings)
            : queryFilters;

        const filterParams = activeQueryFilters
            .sort(function (a, b) {
                // Multi-level sort filters by key and value to prevent possible order depended issues like request caching
                return a.varKey.localeCompare(b.varKey) || a.value.localeCompare(b.value);
            })
            .map(filter => {
                const filterValue = encodeURIComponent(
                    `${filter.varKey}:${
                        (filter.type === 'HIERARCHY' || filter.type === 'DIMENSION') &&
                        filter.exclude
                            ? '!'
                            : ''
                    }${filter.value}`,
                );
                return `filter=${filterValue}`;
            });

        const drillQueryItem = getDrillQueryItem(metadata, settings);
        const settingsParams = [drillQueryItem, conditionQueryItem]
            .filter(isNotNullable)
            .map(setting => `${setting.type}=${encodeURIComponent(setting.key)}`);

        const queryParams = [queryId, ...filterParams, ...settingsParams].join('&');
        return { params: queryParams, metadata };
    },

    externalLink: (metadata, settings, filters, signals, listenToSignalMapping) => {
        const jumpToAO = metadata.querySettings.jumpToAO;
        if (!jumpToAO) return null;

        const { conditionQueryFilter } = getConditionQueryItems(settings, metadata);
        const queryFilters = getActiveQueryFilters(
            signals,
            filters,
            metadata,
            listenToSignalMapping,
            conditionQueryFilter,
            formatRangeAO,
            formatMultipleValuesAO,
        );

        const bwQuerySystem = metadata.querySettings.system || BWQuerySystem.H2R;
        const analysisUrls = {
            [BWQuerySystem.H2R]: 'by-dch2r',
            [BWQuerySystem.H2T]: 'by-dch2t',
            [BWQuerySystem.H2S]: 'by-dch2s',
            [BWQuerySystem.H2D]: 'by-dch2d',
        };

        const baseUrl = `https://${analysisUrls[bwQuerySystem]}.bayer-ag.com:8001/sap/bw/analysis`;
        const application = 'EXCEL';
        const type = jumpToAO.type || 'QUERY';

        const target = jumpToAO.target || metadata.id;
        const filtersUrl = queryFilters.map(
            filter =>
                `var${filter.varKey}=${encodeURIComponent(
                    `${
                        (filter.type === 'HIERARCHY' || filter.type === 'DIMENSION') &&
                        filter.exclude
                            ? '!'
                            : ''
                    }${filter.value}`,
                )}`,
        );

        return `${baseUrl}?APPLICATION=${application}&OBJECT_TYPE=${type}&OBJECT_ID=${target}&${filtersUrl.join(
            '&',
        )}`;
    },

    interactionToFilter: (metadata, listenTo, datapoint) => {
        const filterInteractedWith = metadata.filters.find(
            ({ object }) => object === datapoint.columnKey,
        );
        if (!filterInteractedWith) return null;

        // TODO: handle hierarchies
        // note: we currently not use that for hierarchies, instead we rely on
        // filter hints defined on `listenTo`
        const { filterKey } = splitToHierarchyDotNotation(filterInteractedWith.to);
        const matchedListenTo = listenTo.find(({ item }) => item === filterKey);

        if (!matchedListenTo || !matchedListenTo.interactionEnabled) return null;

        if (matchedListenTo.columnMapping) {
            const columnKeysToMap = Object.keys(matchedListenTo.columnMapping);
            const columnMappedValue = FilterValue.DIMENSION({
                exclude: false,
                value: QueryChartInteractionDatapointUtil.toMultiSelect(datapoint).items.map(
                    item => {
                        if (columnKeysToMap.includes(item.columnKey) && item.datapoint) {
                            const columnMapping =
                                matchedListenTo.columnMapping &&
                                matchedListenTo.columnMapping[item.columnKey];
                            const mappedKey =
                                isNotNullable(columnMapping?.key) &&
                                item.datapoint[columnMapping.key];
                            const mappedValue =
                                isNotNullable(columnMapping?.value) &&
                                item.datapoint[columnMapping.value];

                            return {
                                key: isNotNullable(mappedKey) ? String(mappedKey) : item.key,
                                value: isNotNullable(mappedValue) ? String(mappedValue) : item.key,
                                identifier: isString(mappedKey) ? mappedKey : item.key,
                                internalKey:
                                    matchedListenTo.filterHint?.dimension?.internalKey || '',
                                characteristicId: '',
                                basedOnInternalKey:
                                    matchedListenTo.filterHint?.dimension?.internalKey || '',
                                basedOnCharacteristicId: '',
                            };
                        }

                        return {
                            key: item.key,
                            value: item.value,
                            identifier: item.key,
                            internalKey: matchedListenTo.filterHint?.dimension?.internalKey || '',
                            characteristicId: '',
                            basedOnInternalKey:
                                matchedListenTo.filterHint?.dimension?.internalKey || '',
                            basedOnCharacteristicId: '',
                        };
                    },
                ),
            });
            return { item: filterKey, value: columnMappedValue };
        }

        const value = FilterValue.DIMENSION({
            exclude: false,
            value: QueryChartInteractionDatapointUtil.toMultiSelect(datapoint).items.map(
                ({ key, value }) => ({
                    key: key,
                    value: value,
                    // TODO: following fields are not probably redundant, and we can only create this filter value
                    // in a best-effort manner. For precise and correct values consider using filter hints.
                    identifier: key,
                    internalKey: matchedListenTo.filterHint?.dimension?.internalKey || '',
                    characteristicId: '',
                    basedOnInternalKey: matchedListenTo.filterHint?.dimension?.internalKey || '',
                    basedOnCharacteristicId: '',
                }),
            ),
        });

        return { item: filterKey, value };
    },

    interactionToListenTo: (metadata, listenTo, datapoint) => {
        const filterInteractedWith = metadata.filters.find(
            ({ object }) => object === datapoint.columnKey,
        );

        if (!filterInteractedWith) return null;

        const { filterKey, hierarchyInternalKey } = splitToHierarchyDotNotation(
            filterInteractedWith.to,
        );

        const matchedListenTo = listenTo.find(({ item }) => item === filterKey);

        if (!matchedListenTo) return null;

        const context: MatchedListenToQueryContext = { internalKey: hierarchyInternalKey };

        return { matchedListenTo, context };
    },
};

export function getConditionQueryItems(
    settings: Partial<SettingValueMap>,
    query: BWQuery,
): ConditionQueryItems {
    const condition = {
        conditionQueryItem: settings.CONDITION
            ? { type: SettingType.condition, key: settings.CONDITION.key }
            : null,
        conditionQueryFilter: settings.CONDITION
            ? {
                  exclude: false,
                  key: '',
                  type: 'DIMENSION' as FilterType['DIMENSION'],
                  varKey: query.querySettings.conditionVariableName,
                  value: settings.CONDITION.value.toString(),
                  compounded: false,
              }
            : null,
    };

    return condition;
}

export function getDrillQueryItem(
    query: BWQuery,
    settings: Partial<SettingValueMap>,
): QuerySettingDataItem | null {
    if (!settings.DRILL || !settings.DRILL.length) return null;

    const drillLogic = DrillLogic[query.querySettings.drillLogic || DEFAULT_DRILL_LOGIC];

    return {
        key: `${drillLogic}${getDrillQueryValue(settings.DRILL)}`,
        type: SettingType[query.querySettings.drillDirection],
    };
}

function getDrillQueryValue(drillSetting: SettingValueMap['DRILL']) {
    return sortBy(drillSetting, 'order')
        .map(({ drill }) => drill)
        .join('|');
}

// note: selecting the level from autodrill hierarchy does not happen during `Query.build`.
// it is now expected that the correct level is present on the DRILL setting itself
// https://gitlab.bayer.com/data_architecture/roidocs/-/blob/master/Projects/PortletLibrary/Features/AutoDrill/XML_AutoDrill.md
export function pickDrillFromHierarchy(
    drillHierarchy: string[],
    drillsFromActiveFilters: string[],
): string {
    const invalidIndexes = drillsFromActiveFilters.map(drill => drillHierarchy.indexOf(drill));
    const potentialDrillIndex = Math.max(...invalidIndexes) + 1;
    const drillIndex = clamp(potentialDrillIndex, 0, drillHierarchy.length - 1);

    return drillHierarchy[drillIndex];
}

function getDrillsFromFilters(activeFilters: QueryFilterDataItem[]): string[] {
    return activeFilters.map(({ object }) => object).filter(isNotNullable);
}

function matchFilterKeyToItem(
    item: string,
    filterKey: string,
    listenToSignalMapping: FilterIdToSignalMapping,
) {
    if (!filterKey) return false;
    return item === filterKey || listenToSignalMapping[item] === filterKey;
}

function matchHierarchyFilterKeyToItem(
    item: string,
    filterKey: string,
    hierarchyInternalKeys: string[],
    filterInternalKey: string,
    listenToSignalMapping: FilterIdToSignalMapping,
) {
    const { filterKey: key, hierarchyInternalKey } = splitToHierarchyDotNotation(filterKey);

    if (hierarchyInternalKeys.length === 1)
        return matchFilterKeyToItem(item, key, listenToSignalMapping);
    return (
        matchFilterKeyToItem(item, key, listenToSignalMapping) &&
        hierarchyInternalKey === filterInternalKey
    );
}

function splitToHierarchyDotNotation(to: string): {
    filterKey: string;
    hierarchyInternalKey: string | undefined;
} {
    const [filterKey, hierarchyInternalKey] = to.split('.');

    return { filterKey, hierarchyInternalKey };
}

export function formatRangeBW([from, to]: [string, string]): string {
    return `[${from}|${to}]`;
}

export function formatRangeAO([from, to]: [string, string]): string {
    return `${from} - ${to}`;
}

export function formatMultipleValuesBW(a: string, b: string): string {
    return `${a}|${b}`;
}

export function formatMultipleValuesAO(a: string, b: string): string {
    return `${a}; ${b}`;
}

export function createActiveDrillRespectingAutoDrillHierarchiesFinder(
    settings: Partial<SettingValueMap>,
    filters: FilterData[],
    signals: Signal[],
    query: BWQuery,
    listenToSignalMapping: FilterIdToSignalMapping,
) {
    const { conditionQueryFilter } = getConditionQueryItems(settings, query);
    const activeQueryFilters = getActiveQueryFilters(
        signals,
        filters,
        query,
        listenToSignalMapping,
        conditionQueryFilter,
        formatRangeBW,
        formatMultipleValuesBW,
    );

    const drillsFromActiveFilters = getDrillsFromFilters(activeQueryFilters);

    return (drillKey: string): string => {
        if (!query.querySettings.autoDrillHierarchies) return drillKey;

        const autoDrillHierarchy = query.querySettings.autoDrillHierarchies.find(
            ({ key }) => key === drillKey,
        );

        if (!autoDrillHierarchy) return drillKey;

        return pickDrillFromHierarchy(autoDrillHierarchy.hierarchy, drillsFromActiveFilters);
    };
}

export function omitFiltersInCurrentDrill(
    filters: QueryFilterDataItem[],
    settings: Partial<SettingValueMap>,
) {
    if (!settings.DRILL || !settings.DRILL.length) return filters;

    const currentDrillKeys = settings.DRILL.map(({ key }) => key);

    return filters.filter(filter => !filter.object || !currentDrillKeys.includes(filter.object));
}

export function getActiveQueryFilterDataItems(
    signals: Signal[],
    filters: FilterData[],
    query: BWQuery,
    listenToSignalMapping: FilterIdToSignalMapping,
    conditionQueryFilter: QueryFilterDataItem | null,
    formatRange: (a: [string, string]) => string,
): QueryFilterDataItem[] {
    const signalsAsFilters = signals
        .filter(signal => !filters.find(filter => filter.item === signal.name))
        .map(signal => {
            const filter = FilterValue.tryParse(signal.value);
            if (!filter) return null;
            return {
                item: signal.name,
                value: filter,
            };
        })
        .filter(isNotNullable);

    const signalsAndFilters = [...filters, ...signalsAsFilters];

    const filterQueryValues = signalsAndFilters
        .map(({ item, value }) => {
            return FilterValue.match<QueryFilterDataItem[] | null>(value, {
                DIMENSION: ({ value, type, exclude }) =>
                    value
                        .map(({ internalKey, key }) => {
                            const queryItems = query.filters.filter(filter =>
                                matchFilterKeyToItem(item, filter.to, listenToSignalMapping),
                            );
                            if (isEmpty(queryItems)) return null;

                            return queryItems.map(queryItem => ({
                                varKey: queryItem.variable || queryItem.object,
                                key: internalKey,
                                value: key,
                                type,
                                exclude: exclude ?? false,
                                object: queryItem.object,
                                compounded: queryItem.compounded ?? false,
                            }));
                        })
                        .flat(1)
                        .filter(isNotNullable),
                HIERARCHY: ({ value, type, exclude }) => {
                    return (
                        value.values
                            // depending on the source of the filter (and thus its effect)
                            // we may not apply the hierarchy filter (see 3167: Hierarchy filtering - don't autoselect upstream)
                            .filter(({ effect }) => effect !== 'disabled')
                            .map(({ internalKey, key, nodeName, hierarchyVariantId }) => {
                                const queryItems = query.filters.filter(filter =>
                                    matchHierarchyFilterKeyToItem(
                                        item,
                                        filter.to,
                                        value.hierarchyInternalKeys,
                                        internalKey,
                                        listenToSignalMapping,
                                    ),
                                );

                                if (isEmpty(queryItems)) return null;

                                const hierarchyValuesFilters = queryItems.map(queryItem => ({
                                    varKey: queryItem.variable || queryItem.object,
                                    key: internalKey,
                                    value: `${key}${nodeName ? `:${nodeName}` : ''}`,
                                    nodeName: nodeName,
                                    type,
                                    exclude: exclude ?? false,
                                    object: queryItem.object,
                                    compounded: queryItem.compounded ?? false,
                                }));

                                if (!hierarchyVariantId) return [hierarchyValuesFilters];

                                const queryVariantItems = query.filters.filter(
                                    filter => filter.to === internalKey + HIERARCHY_VARIANT_SUFFIX,
                                );

                                if (isEmpty(queryVariantItems)) return null;

                                const hierarchyVariantValueFilters = queryVariantItems.map(
                                    (queryVariantItem, index) => ({
                                        varKey:
                                            queryVariantItem.variable || queryItems[index].object,
                                        key: internalKey + HIERARCHY_VARIANT_SUFFIX,
                                        value: hierarchyVariantId,
                                        type,
                                        nodeName: '',
                                        exclude: false,
                                        object: queryItems[index].object,
                                        compounded: queryVariantItem.compounded ?? false,
                                    }),
                                );

                                return [hierarchyValuesFilters, hierarchyVariantValueFilters];
                            })
                            .flat(2)
                            .filter(isNotNullable)
                    );
                },
                DATE: ({ value, type }) => {
                    const queryItems = query.filters.filter(filter =>
                        matchFilterKeyToItem(item, filter.to, listenToSignalMapping),
                    );
                    if (isEmpty(queryItems)) return null;

                    return queryItems.map(queryItem => ({
                        varKey: queryItem.variable || queryItem.object,
                        key: item,
                        // roi5 filters are iso timestamps in utc
                        value: DateTime.fromISO(value, { zone: 'utc' }).toFormat(
                            queryItem?.dateFormat ?? DATE_FORMAT,
                        ),
                        type,
                        object: queryItem.object,
                        compounded: queryItem.compounded ?? false,
                    }));
                },
                DATE_RANGE: ({ value, type }) => {
                    const queryItems = query.filters.filter(filter =>
                        matchFilterKeyToItem(item, filter.to, listenToSignalMapping),
                    );

                    if (isEmpty(queryItems)) return null;

                    return queryItems.map(queryItem => {
                        // roi5 filters are iso timestamps in utc
                        const from = DateTime.fromISO(value[0], { zone: 'utc' }).toFormat(
                            queryItem?.dateFormat ?? DATE_FORMAT,
                        );
                        const to = DateTime.fromISO(value[1], { zone: 'utc' }).toFormat(
                            queryItem?.dateFormat ?? DATE_FORMAT,
                        );

                        return {
                            varKey: queryItem.variable || queryItem.object,
                            key: item,
                            value: formatRange([from, to]),
                            type,
                            object: queryItem.object,
                            compounded: queryItem.compounded ?? false,
                        };
                    });
                },
                SCALE: () => null,
                SMART_VIEW_TAG: () => null,
                ROLE: ({ value, type }) => {
                    const queryItems = query.filters.filter(filter =>
                        matchFilterKeyToItem(item, filter.to, listenToSignalMapping),
                    );
                    if (isEmpty(queryItems)) return null;

                    return queryItems.map(queryItem => ({
                        varKey: queryItem.variable || queryItem.object,
                        key: item,
                        value: value.key,
                        type,
                        object: queryItem.object,
                        compounded: queryItem.compounded ?? false,
                    }));
                },
                NUMBER: ({ value, type }) => {
                    const queryItems = query.filters.filter(filter =>
                        matchFilterKeyToItem(item, filter.to, listenToSignalMapping),
                    );

                    if (isEmpty(queryItems)) return null;

                    return queryItems.map(queryItem => ({
                        varKey: queryItem.variable || queryItem.object,
                        key: item,
                        value: String(value),
                        type,
                        object: queryItem.object,
                        compounded: queryItem.compounded ?? false,
                    }));
                },
            });
        })
        .flat(1)
        .filter(isNotNullable);

    const queryFiltersDefault: QueryFilterDataItem[] = query.filters
        .map(filter => {
            const { default: filterDefault } = filter;
            if (!filterDefault) return null;

            return filterDefault.value.map(val => ({
                varKey: filter.variable || filter.object,
                key: filter.to,
                value: `${val}${filterDefault.nodeName ? `:${filterDefault.nodeName}` : ''}`,
                type: (filterDefault.nodeName ? 'HIERARCHY' : 'DIMENSION') as
                    | FilterType['HIERARCHY']
                    | FilterType['DIMENSION'],
                exclude: filterDefault.exclude ?? false,
                object: filter.object,
                compounded: filter.compounded ?? false,
                ...(filterDefault.nodeName && { nodeName: filterDefault.nodeName }),
            }));
        })
        .filter(isNotNullable)
        .flat(1);

    const uniqueDefaults = differenceBy(queryFiltersDefault, filterQueryValues, 'varKey');

    const valuesWithDefaults = [...filterQueryValues, ...uniqueDefaults];

    //condition query filter has to override filter which have same variable
    //name as specified in `query.querySettings.conditionVariableName`
    const outFilters = conditionQueryFilter
        ? [
              ...valuesWithDefaults.filter(filter => filter.varKey !== conditionQueryFilter.varKey),
              conditionQueryFilter,
          ]
        : valuesWithDefaults;

    return outFilters.map(applyCompoundedKeyLogic);
}

export function formatMultipleFilterDataItems(
    queryDataItems: QueryFilterDataItem[],
    formatMultiple: (a: string, b: string) => string,
): QueryFilterDataItem[] {
    const [singleValueFilters, multipleValueFilters] = partition(
        [...queryDataItems],
        filter =>
            (filter.type === 'HIERARCHY' && filter.nodeName) ||
            ['DATE', 'DATE_RANGE'].includes(filter.type),
    );

    const formatedFilters = [
        ...singleValueFilters,
        ...Object.entries(groupBy(multipleValueFilters, 'varKey')).map(([_, value]) => ({
            ...value.reduce((prev, curr) => ({
                ...prev,
                value: formatMultiple(prev.value, curr.value),
            })),
        })),
    ];

    return formatedFilters;
}

export function getActiveQueryFilters(
    signals: Signal[],
    filters: FilterData[],
    query: BWQuery,
    listenToSignalMapping: FilterIdToSignalMapping,
    conditionQueryFilter: QueryFilterDataItem | null,
    formatRange: (a: [string, string]) => string,
    formatMultiple: (a: string, b: string) => string,
): QueryFilterDataItem[] {
    const queryFilterDataItems = getActiveQueryFilterDataItems(
        signals,
        filters,
        query,
        listenToSignalMapping,
        conditionQueryFilter,
        formatRange,
    );
    const formatedFilters = formatMultipleFilterDataItems(queryFilterDataItems, formatMultiple);

    return formatedFilters;
}

/**
 * Compounded logic means that before sending a filter value to the back-end we drop everything before and including the last '/' character.
 * As an example, if the original filter value was "ABC/DEF/GHJ", the logic would send only "GHJ" instead of the complete string.
 */
function applyCompoundedKeyLogic(queryItem: QueryFilterDataItem): QueryFilterDataItem {
    return queryItem.compounded
        ? { ...queryItem, value: last(queryItem.value.split('/')) ?? queryItem.value }
        : queryItem;
}
